import { Component, OnInit, OnDestroy, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ResultService } from '../services/result.service';
import { Subscription } from 'rxjs';
import { TranslationService } from '../services/translation.service';
import { TranslatePipe } from '../pipes/translate.pipe';
import { RtlService } from '../services/rtl.service';
import { CircularMeterComponent } from '../shared/components/circular-meter/circular-meter.component';
import { SparklineComponent } from '../shared/components/sparkline/sparkline.component';
import { ResultShellComponent } from '../shared/components/result-shell/result-shell.component';
import { StorageService } from '../services/storage.service';
import { RestService } from '../services/rest.service';
import { PaymentCodeService } from '../services/payment-code.service';
import { getSeverityLevelByValue } from 'src/utils/severity.utils';
import { GtmAnalyticsService } from '../services/gtm-analytics.service';
import { getCurrencySymbol } from '../admin/currencies';
import { ErrorReportingService } from '../services/error-reporting.service';

interface SnapshotMetric {
  titleKey: string;
  descriptionKey: string;
  value: number;
  scanHistory: number[];
  scanDates: string[];
  vsLast: number;
  vsBaseline: number;
}

interface SnapshotData {
  metrics: SnapshotMetric[];
  startDate: string;
  checkDate: string;
  checkCount: number;
  daysSinceLast: number;
}

@Component({
  selector: 'app-trial-result',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    TranslatePipe,
    CircularMeterComponent,
    SparklineComponent,
    ResultShellComponent,
  ],
  templateUrl: './trial-result.component.html',
  styleUrl: './trial-result.component.scss',
})
export class TrialResultComponent implements OnInit, OnDestroy {
  scanId: string = '';
  loading: boolean = true;
  error: string = '';

  snapshotData: SnapshotData | null = null;
  singleScanPrice: string = '29';
  currencySymbol: string = '$';

  private visualizationsInitialized = false;
  showFullExplanationMap: { [key: number]: boolean } = {
    0: false,
    1: false,
    2: false,
    3: false,
  };

  private subscription: Subscription = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private resultService: ResultService,
    private translationService: TranslationService,
    private elementRef: ElementRef,
    public rtlService: RtlService,
    private storageService: StorageService,
    private restService: RestService,
    private paymentCodeService: PaymentCodeService,
    private gtmAnalytics: GtmAnalyticsService,
    private reporter: ErrorReportingService,
  ) {
    console.log('TrialResultComponent: initialized');
  }

  async ngOnInit() {
    this.scanId = this.route.snapshot.params['scanId'];

    if (!this.scanId) {
      this.router.navigate(['/app/new-test']);
      return;
    }

    await this.loadPriceData();
    await this.loadData();
  }

  async loadPriceData() {
    console.log('TrialResultComponent: loading price data');

    await this.paymentCodeService.saveCodeAndFetchPrice(
      undefined,
      this.scanId,
    );

    let priceData = this.storageService.getPriceData();

    if (!priceData) {
      console.log(
        'TrialResultComponent: No price data in localStorage, fetching default prices from backend',
      );
      try {
        const response = await this.restService.request<any>({
          path: 'payment/decode-price',
          method: 'GET',
          queryParams: { ...(this.scanId ? { scanId: this.scanId } : {}), lang: this.translationService.getCurrentLanguage() },
        });

        if (response) {
          priceData = response;
          this.storageService.setPriceData(response);
          console.log(
            'TrialResultComponent: Fetched and saved default price data',
            priceData,
          );
        }
      } catch (error) {
        console.error(
          'TrialResultComponent: Error fetching default prices:',
          error,
        );
        this.reporter.report(error, 'results');
      }
    } else {
      console.log(
        'TrialResultComponent: loaded price data from localStorage',
        priceData,
      );
    }

    if (priceData) {
      // Never render "0₪" (N57-10): keep the non-zero default if admin/decode
      // returned 0 or empty for the single-scan price.
      const p = Number(priceData.price);
      this.singleScanPrice =
        Number.isFinite(p) && p > 0 ? String(priceData.price) : this.singleScanPrice;
      this.currencySymbol = getCurrencySymbol(priceData.currency);
      console.log(
        `TrialResultComponent: single scan price set to: ${this.singleScanPrice}, currencySymbol: ${this.currencySymbol}`,
      );
    }
  }

  async loadData() {
    this.loading = true;
    this.visualizationsInitialized = false;
    this.snapshotData = null;

    try {
      const trialData = await this.resultService.getTrialResults(this.scanId);

      if (trialData && trialData.history.length > 0) {
        this.snapshotData = this.processTrialData(trialData);
      } else {
        this.snapshotData = this.getMockData();
      }

      this.loading = false;
      this.gtmAnalytics.trackFinishedScan();
      setTimeout(() => {
        this.initializeVisualizations();
        this.gtmAnalytics.trackPaywallViewed();
      }, 100);
    } catch (error: any) {
      console.error('TrialResultComponent: error loading data:', error);
      // 303 (already paid) and 401 (auth) are expected redirects, not failures.
      if (error?.status !== 303 && error?.status !== 401) {
        this.reporter.report(`trial-result load failed status=${error?.status}`, 'results');
      }

      if (error.status === 303) {
        console.log(
          'TrialResultComponent: scan is already paid, redirecting to full results',
        );
        this.router.navigate(['/app/results', this.scanId]);
        return;
      }

      if (error.status === 401) {
        this.error = this.translationService.translate(
          'results.errors.authenticationRequired',
        );
        this.router.navigate(['/app/login']);
        return;
      }

      this.error = this.translationService.translate(
        'results.snapshot.failedToLoad',
      );
      this.loading = false;
    }
  }

  processTrialData(trialData: {
    markableIndex: number;
    history: number[];
    dates: string[];
    vsLast: number;
    vsBaseline: number;
    startDate: string;
    checkDate: string;
    checkCount: number;
    daysSinceLast: number;
  }): SnapshotData {
    console.log(
      'TrialResultComponent: processTrialData - markableIndex:',
      trialData.markableIndex,
    );

    const mockDates =
      trialData.dates.length > 0 ? trialData.dates : [new Date().toISOString()];

    return {
      metrics: [
        {
          titleKey: 'results.snapshot.hormonalBalance',
          descriptionKey: 'results.snapshot.hormonalBalanceDescription',
          value: trialData.markableIndex,
          scanHistory: trialData.history,
          scanDates: trialData.dates,
          vsLast: trialData.vsLast,
          vsBaseline: trialData.vsBaseline,
        },
        {
          titleKey: 'results.snapshot.hormonalImprint',
          descriptionKey: 'results.snapshot.hormonalImprintDescription',
          value: 18,
          scanHistory: [15, 16, 17, 16, 18, 19, 18, 18],
          scanDates: mockDates,
          vsLast: 0,
          vsBaseline: 3,
        },
        {
          titleKey: 'results.snapshot.sharpScale',
          descriptionKey: 'results.snapshot.sharpScaleDescription',
          value: 82,
          scanHistory: [70, 73, 75, 78, 80, 81, 79, 82],
          scanDates: mockDates,
          vsLast: 3,
          vsBaseline: 12,
        },
        {
          titleKey: 'results.snapshot.symptomBurden',
          descriptionKey: 'results.snapshot.symptomBurdenDescription',
          value: 45,
          scanHistory: [62, 58, 55, 52, 50, 48, 47, 45],
          scanDates: mockDates,
          vsLast: -2,
          vsBaseline: -17,
        },
      ],
      startDate: trialData.startDate,
      checkDate: trialData.checkDate,
      checkCount: trialData.checkCount,
      daysSinceLast: trialData.daysSinceLast,
    };
  }

  getMockData(): SnapshotData {
    console.log('TrialResultComponent: using mock data');

    const mockDates = [
      '2024-01-15',
      '2024-02-12',
      '2024-03-18',
      '2024-04-22',
      '2024-05-27',
      '2024-07-01',
      '2024-08-19',
      '2024-10-07',
    ];

    return {
      metrics: [
        {
          titleKey: 'results.snapshot.hormonalBalance',
          descriptionKey: 'results.snapshot.hormonalBalanceDescription',
          value: 62,
          scanHistory: [78, 75, 72, 68, 65, 64, 63, 62],
          scanDates: mockDates,
          vsLast: -1,
          vsBaseline: -16,
        },
        {
          titleKey: 'results.snapshot.hormonalImprint',
          descriptionKey: 'results.snapshot.hormonalImprintDescription',
          value: 18,
          scanHistory: [15, 16, 17, 16, 18, 19, 18, 18],
          scanDates: mockDates,
          vsLast: 0,
          vsBaseline: 3,
        },
        {
          titleKey: 'results.snapshot.sharpScale',
          descriptionKey: 'results.snapshot.sharpScaleDescription',
          value: 82,
          scanHistory: [70, 73, 75, 78, 80, 81, 79, 82],
          scanDates: mockDates,
          vsLast: 3,
          vsBaseline: 12,
        },
        {
          titleKey: 'results.snapshot.symptomBurden',
          descriptionKey: 'results.snapshot.symptomBurdenDescription',
          value: 45,
          scanHistory: [62, 58, 55, 52, 50, 48, 47, 45],
          scanDates: mockDates,
          vsLast: -2,
          vsBaseline: -17,
        },
      ],
      startDate: '2024-01-15',
      checkDate: '2024-10-07',
      checkCount: 8,
      daysSinceLast: 14,
    };
  }

  initializeVisualizations() {
    if (!this.snapshotData || this.visualizationsInitialized) {
      return;
    }

    this.visualizationsInitialized = true;
  }

  toggleFullExplanation(index: number) {
    this.showFullExplanationMap[index] = !this.showFullExplanationMap[index];
  }

  getFullExplanationText(index: number): string {
    const translationKeys = [
      'results.snapshot.hormonalBalanceExplanation',
      'results.snapshot.hormonalImprintExplanation',
      'results.snapshot.sharpScaleExplanation',
      'results.snapshot.symptomBurdenExplanation',
    ];
    return this.translationService.translate(
      translationKeys[index] || translationKeys[0],
    );
  }

  getAlignmentText(index: number): string {
    const translationKeys = [
      'results.snapshot.hormonalBalanceAlignmentText',
      'results.snapshot.hormonalImprintAlignmentText',
      'results.snapshot.sharpScaleAlignmentText',
      'results.snapshot.symptomBurdenAlignmentText',
    ];
    return this.translationService.translate(
      translationKeys[index] || translationKeys[0],
    );
  }

  getMetricLabels(metricIndex: number): {
    high: string;
    medium: string;
    low: string;
  } {
    const metricKeys = [
      'hormonalBalance',
      'hormonalImprint',
      'sharpScale',
      'symptomBurden',
    ];

    const metricKey = metricKeys[metricIndex] || metricKeys[0];

    return {
      high: this.translationService.translate(
        `results.snapshot.severityLabels.${metricKey}.high`,
      ),
      medium: this.translationService.translate(
        `results.snapshot.severityLabels.${metricKey}.medium`,
      ),
      low: this.translationService.translate(
        `results.snapshot.severityLabels.${metricKey}.low`,
      ),
    };
  }

  getSeverityLevel(
    value: number,
    metricIndex: number = 0,
    invertColors: boolean = false,
  ): {
    label: string;
    class: string;
    color: string;
    restColor: string;
  } {
    const labels = this.getMetricLabels(metricIndex);
    return getSeverityLevelByValue(value, { labels, invertColors }) as {
      label: string;
      class: string;
      color: string;
      restColor: string;
    };
  }

  isBlurredSection(index: number): boolean {
    return true;
  }

  goBack() {
    this.router.navigate(['/app/new-test']);
  }

  navigateToPayment() {
    this.router.navigate(['/app/payment'], {
      queryParams: { scanId: this.scanId },
    });
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
}
